import { useState, useCallback, useEffect } from "react";
import { Car, Check, Activity, Clock, XCircle, AlertCircle } from "lucide-react";
import type { CallPayload } from "@zoidberg/contract";
import { T, seamBg, bufferBg } from "../theme";
import { Eyebrow, Card, Pill } from "../components";
import { instantTo12h, hhmmTo12h } from "../lib/format";
import { fetchOverride, setOverride, clearOverride, type DailyOverride } from "../api/override";

/**
 * TODAY — live-wired. The hero wake time, Hard Wake, and the derivation line all
 * come from the real compiler output (`buildPayload`). "Computed never means
 * opaque" (§4.1) is literally true here: the explanation string is the solver's.
 */
export function Today({ payload, tz }: { payload: CallPayload; tz: string }) {
  const [override, setOverrideState] = useState<DailyOverride | null>(null);
  const [loading, setLoading] = useState(false);
  const [overrideTime, setOverrideTime] = useState("");

  // Load existing override whenever the payload date changes.
  useEffect(() => {
    fetchOverride(payload.date).then((o) => {
      setOverrideState(o);
    });
  }, [payload.date]);

  const wake = instantTo12h(payload.wake.wakeTime, tz);
  const hard = instantTo12h(payload.wake.hardWake, tz);
  const fe = payload.script.firstEvent;

  const applyOverride = useCallback(async () => {
    if (!overrideTime) return;
    const parts = overrideTime.split(":").map(Number);
    if (parts.length !== 2 || parts.some(Number.isNaN)) return;
    const [h, m] = parts as [number, number];
    setLoading(true);
    try {
      // Convert "HH:MM" to an ISO instant for the given date + tz
      const dateObj = new Date(`${payload.date}T00:00:00`);
      dateObj.setHours(h, m, 0, 0);
      const iso = dateObj.toISOString();
      const o = await setOverride({ date: payload.date, wakeTimeOverride: iso, cancelled: false });
      setOverrideState(o);
      setOverrideTime("");
    } finally {
      setLoading(false);
    }
  }, [overrideTime, payload.date]);

  const cancelToday = useCallback(async () => {
    setLoading(true);
    try {
      const o = await setOverride({ date: payload.date, cancelled: true });
      setOverrideState(o);
    } finally {
      setLoading(false);
    }
  }, [payload.date]);

  const clearToday = useCallback(async () => {
    setLoading(true);
    try {
      await clearOverride(payload.date);
      setOverrideState(null);
    } finally {
      setLoading(false);
    }
  }, [payload.date]);

  return (
    <div style={{ padding: "18px 16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Eyebrow>Tomorrow · {payload.date}</Eyebrow>
        <Pill color={override?.cancelled ? T.dawn : T.mint} bg={override?.cancelled ? "rgba(255,107,69,.12)" : "rgba(130,220,164,.12)"}>
          {override?.cancelled ? "● cancelled" : "● armed"}
        </Pill>
      </div>

      {/* dawn arc + wake hero */}
      <div style={{ position: "relative", textAlign: "center", margin: "22px 0 8px", padding: "26px 0 10px" }}>
        <div
          style={{
            position: "absolute",
            inset: "0 8px auto",
            height: 120,
            borderRadius: "50% 50% 0 0 / 100% 100% 0 0",
            background:
              "radial-gradient(ellipse at 50% 100%, rgba(255,107,69,.32) 0%, rgba(255,180,84,.12) 45%, transparent 72%)",
            borderTop: "1px solid rgba(255,180,84,.35)",
          }}
        />
        <Eyebrow color={override?.cancelled ? T.dawn : T.gold}>
          {override?.cancelled ? "wake cancelled — no calls today" : "wake time · solved tonight"}
        </Eyebrow>
        <div
          className="mono"
          style={{ fontSize: 64, fontWeight: 300, color: override?.cancelled ? T.dim : T.paper, lineHeight: 1.05, letterSpacing: "-0.03em", position: "relative" }}
        >
          {override?.cancelled ? "—" : wake.h}
          {!override?.cancelled && <span style={{ fontSize: 22, color: T.gold }}> {wake.ampm}</span>}
        </div>
        {!override?.cancelled && (
          <div style={{ fontSize: 12, color: T.mist, marginTop: 2 }}>
            hard wake{" "}
            <span className="mono" style={{ color: T.dawn, fontWeight: 600 }}>
              {hard.h}
            </span>
          </div>
        )}
        <div className="mono" style={{ fontSize: 9.5, color: T.dim, marginTop: 6, letterSpacing: "0.04em", padding: "0 12px" }}>
          {payload.wake.wakeExplanation}
        </div>
      </div>

      {/* Override section */}
      <Card style={{ borderColor: override?.cancelled ? "rgba(255,107,69,.4)" : "rgba(255,180,84,.4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
          <Clock size={12} color={override?.cancelled ? T.dawn : T.gold} />
          <Eyebrow color={override?.cancelled ? T.dawn : T.gold}>daily override</Eyebrow>
        </div>

        {override?.wakeTimeOverride && !override.cancelled && (
          <div style={{ fontSize: 13, color: T.paper, marginBottom: 10 }}>
            Override set: <span className="mono" style={{ color: T.gold }}>{instantTo12h(override.wakeTimeOverride, tz).h} {instantTo12h(override.wakeTimeOverride, tz).ampm}</span>
          </div>
        )}

        {override?.cancelled && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <AlertCircle size={14} color={T.dawn} />
            <span style={{ fontSize: 13, color: T.dawn }}>All wake calls cancelled for today.</span>
          </div>
        )}

        {!override?.cancelled && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              type="time"
              value={overrideTime}
              onChange={(e) => setOverrideTime(e.target.value)}
              disabled={loading}
              style={{
                background: T.night,
                border: `1px solid ${T.edge}`,
                borderRadius: 8,
                padding: "8px 10px",
                color: T.paper,
                fontSize: 13,
                outline: "none",
                flex: 1,
              }}
            />
            <button
              onClick={applyOverride}
              disabled={!overrideTime || loading}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                background: T.gold,
                color: T.night,
                border: "none",
                fontSize: 12,
                fontWeight: 600,
                cursor: overrideTime && !loading ? "pointer" : "not-allowed",
                opacity: overrideTime && !loading ? 1 : 0.5,
              }}
            >
              Set wake time
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 8 }}>
          {!override?.cancelled ? (
            <button
              onClick={cancelToday}
              disabled={loading}
              style={{
                flex: 1,
                padding: "8px 0",
                borderRadius: 8,
                background: "rgba(255,107,69,.12)",
                color: T.dawn,
                border: `1px solid rgba(255,107,69,.3)`,
                fontSize: 12,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
              }}
            >
              Cancel today's calls
            </button>
          ) : (
            <button
              onClick={clearToday}
              disabled={loading}
              style={{
                flex: 1,
                padding: "8px 0",
                borderRadius: 8,
                background: "rgba(130,220,164,.12)",
                color: T.mint,
                border: `1px solid rgba(130,220,164,.3)`,
                fontSize: 12,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
              }}
            >
              Re-enable calls
            </button>
          )}

          {(override?.wakeTimeOverride || override?.cancelled) && (
            <button
              onClick={clearToday}
              disabled={loading}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                background: "transparent",
                color: T.dim,
                border: `1px solid ${T.edge}`,
                fontSize: 12,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              <XCircle size={14} />
            </button>
          )}
        </div>
      </Card>

      {/* first action / first event */}
      <Card style={{ marginTop: 14, borderColor: "rgba(255,180,84,.4)" }}>
        <Eyebrow color={T.gold}>tomorrow opens with</Eyebrow>
        <div style={{ fontSize: 15, color: T.paper, fontWeight: 600, margin: "6px 0 2px" }}>
          {fe ? fe.label : "Nothing fixed — a soft open"}
        </div>
        {fe?.leaveBy && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: T.mist }}>
            <Car size={13} color={T.gold} />
            <span>
              leave by{" "}
              <span className="mono" style={{ color: T.paper, fontWeight: 600 }}>
                {hhmmTo12h(fe.leaveBy)}
              </span>
            </span>
          </div>
        )}
      </Card>

      {/* day shape ribbon */}
      <div style={{ marginTop: 14 }}>
        <Eyebrow>tomorrow's shape</Eyebrow>
        <div style={{ display: "flex", gap: 3, marginTop: 8, height: 26, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ flex: 2.4, background: "rgba(130,220,164,.25)", borderLeft: `3px solid ${T.mint}` }} />
          <div style={{ flex: 0.7, background: seamBg }} />
          <div style={{ flex: 3.2, background: "rgba(255,180,84,.22)", borderLeft: `3px solid ${T.gold}` }} />
          <div style={{ flex: 0.6, background: bufferBg }} />
          <div style={{ flex: 2, background: "rgba(154,146,188,.18)", borderLeft: `3px solid ${T.mist}` }} />
        </div>
        <div className="mono" style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.dim, marginTop: 5 }}>
          <span>morning</span>
          <span>first event</span>
          <span>afternoon</span>
        </div>
      </div>

      {/* health strip */}
      <Card style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Activity size={14} color={T.mint} />
          <span style={{ fontSize: 12, color: T.mist }}>Test call passed 9:02 PM · payload synced</span>
        </div>
        <Check size={14} color={T.mint} />
      </Card>
    </div>
  );
}
